import { Component, Input, OnDestroy, SimpleChanges } from '@angular/core'
import * as Highcharts from 'highcharts'

@Component({
    selector: 'app-ttm-sparkline-chart',
    templateUrl: './ttm-sparkline-chart.component.html',
    standalone: false
})
export class TtmSparklineChartComponent implements OnDestroy {
  @Input() chartData: any

  public highChart: typeof Highcharts = Highcharts
  public highChartOptions: Highcharts.Options

  private _chartData: {
    travel: number[]
    time: string[]
    command: string[]
    rating: number[]
  }

  public getHighChartOptions(): Highcharts.Options {
    const travel: number[] = this._chartData.travel
    const command: string[] = this._chartData.command
    const time: string[] = this._chartData.time
    const rating: number[] = this._chartData.rating
    const self: this = this
    const maxTT: number = Math.abs(Math.max(...travel))

    return {
      chart: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        width: 200,
        height: 120,
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        hideDelay: 0,
        outside: true,
        formatter: function(): string {
          const _travel: number = travel[this.point.index] ? travel[this.point.index] : null
          const _command: string = command[this.point.index] ? command[this.point.index] : null
          const _time: string = time[this.point.index] ? time[this.point.index] : null
          return `<div>
            <span>Traveltime: <b>${_travel > 0 ? _travel + 's' : 'No Response'}</b></span>
            <br>
            <span><b>${_command}</b></span>
            <br>
            <span>Time:</span>
            <br>
            <span><b>${self.formatDate(_time)}</b></span>
          </div>`
        },
        style: {
          fontSize: '12px',
          fontFamily: 'Arial, sans-serif',
          zIndex: 99999,
        },
      },
      plotOptions: {
        series: {
          animation: false,
          shadow: false,
          lineWidth: 1,
        },
        column: {
          stacking: 'normal',
          opacity: 0.9,
          pointWidth: 14,
        },
      },
      yAxis: {
        max: maxTT,
        lineWidth: 1,
        labels: {
          enabled: true,
          formatter: function(): string {
            return `${this.value}`
          },
          style: {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
          },
        },
        title: {
          text: null,
        },
      },
      xAxis: {
        visible: false,
        labels: {
          enabled: false,
        },
      },
      series: [
        {
          type: 'column',
          data: this.getDataSeries(travel, maxTT, rating),
        },
      ],
    }
  }

  private getDataSeries(travel: number[], maxTT: number, rating: number[]): any[] {
    if (this.hasOnlyNoResponse(travel)) {
      return [
        ...travel.map((v: number, index: number) => {
          return {
            y: 1,
            color: '#FF0000',
            borderColor: '#FF0000',
            borderWidth: 1.5,
          }
        }),
      ]
    }
    return [
      ...travel.map((v: number, index: number) => {
        return {
          y: v > 0 ? v : Math.max(maxTT, 1),
          color: v > 0 ? this.getColumnColor(rating[index]) : '#FF0000',
          borderColor: v > 0 ? this.getBorderColor(rating[index]) : '#FF0000',
          borderWidth: 1.5,
        }
      }),
    ]
  }

  private hasOnlyNoResponse(travel: number[]): boolean {
    const onlyNoResponse: number[] = travel.filter(t => t < 0)
    return onlyNoResponse.length === travel.length
  }

  private getColumnColor(rating: number): string {
    if (rating >= 0 && rating < 3) {
      return '#ffd9d9'
    } else if (rating >= 3 && rating < 6) {
      return '#fff2d9'
    } else if (rating >= 6 && rating < 9) {
      return '#ffffd9'
    } else if (rating >= 9 && rating < 11) {
      return '#ddfbdf'
    } else if (rating >= 11) {
      return '#50a0e1'
    }
  }

  private getBorderColor(rating: number): string {
    if (rating >= 0 && rating < 3) {
      return '#FF0000'
    } else if (rating >= 3 && rating < 6) {
      return '#FFAA00'
    } else if (rating >= 6 && rating < 9) {
      return '#1dba5c'
    } else if (rating >= 9 && rating < 11) {
      return '#1dba5c'
    } else if (rating >= 11) {
      return '#50a0e1'
    }
  }

  private formatDate(date: string): string {
    const elemFullDate: string[] = date
      .replace('T', ' ')
      .replace('Z', '')
      .split(' ')
    const elemDate = elemFullDate[0].split('-')
    if (elemFullDate.length < 2) {
      return `${elemDate[2]}-${elemDate[1]}-${elemDate[0]}`
    }
    return `${elemDate[2]}-${elemDate[1]}-${elemDate[0]} ${elemFullDate[1]}`
  }

  private _drawHighChart(): void {
    if (this.hasData()) {
      this.highChartOptions = this.getHighChartOptions()
    } else {
      this.highChartOptions = undefined
    }
  }

  public hasData(): boolean {
    return this._chartData && this._chartData.travel.length > 0
  }

  ngOnChanges(change: SimpleChanges): void {
    if (JSON.stringify(this._chartData) != JSON.stringify(change.chartData.currentValue)) {
      setTimeout((): void => {
        this._chartData = change.chartData.currentValue
        this._drawHighChart()
      }, 100)
    }
  }

  ngOnDestroy(): void {
    this._chartData = undefined
    this.highChartOptions = undefined
  }
}
